import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import './Navbar.css';
import {
  logout,
  isLoggedIn,
  getUser,
  getToken,
  getRefreshToken,
  setSession,
  getUserRole,
} from '../../utils/auth';
import { UserProfileService } from '../../services/UserProfileService';
import { apiUrls } from '../../services/ApiUrls';
import { Avatar, Dropdown, Menu, Progress } from 'antd';
import {
  parseProfileCompletionPercent,
  STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD,
} from '../../utils/studentProfileLoginFlow';
import {
  InboxOutlined,
  CrownOutlined,
  TeamOutlined,
  LinkedinOutlined,
  XOutlined,
  InstagramOutlined,
  FacebookOutlined,
} from '@ant-design/icons';
import MenuLogin from '../common/MenuLogin';
import MenuRegistration from '../common/MenuRegistration';
import NotificationDropdown from '../notifications/NotificationDropdown';
import { ROUTES } from '../../config/routes';
import { SOCIAL_LINKS } from '../../config/siteLinks';

/** Blue circle + white outline figure when user has no profile photo (file in `public/images/`). */
const DEFAULT_NAV_PLACEHOLDER_SRC = '/images/default-profile-placeholder.svg';

const getDashboardPath = (role?: string): string => {
  switch (role) {
    case 'ORGANIZATION':
      return ROUTES.ORGANIZATION.DASHBOARD;
    case 'MENTOR':
      return ROUTES.MENTOR.DASHBOARD;
    case 'SPONSOR':
      return ROUTES.SPONSOR.DASHBOARD;
    default:
      return ROUTES.STUDENT.DASHBOARD;
  }
};

/** Portal routes (dashboard, jobs, internships, etc.) highlight "My Dashboard" in the nav. */
const isDashboardNavActive = (role: string | undefined, pathname: string): boolean => {
  switch (role) {
    case 'ORGANIZATION':
      return pathname.startsWith('/organization/');
    case 'STUDENT':
      return pathname.startsWith('/student/') || pathname.startsWith('/profile/');
    case 'MENTOR':
      return pathname.startsWith('/mentor/') || pathname.startsWith('/mentorship/');
    case 'SPONSOR':
      return pathname.startsWith('/sponsor/');
    default:
      return false;
  }
};

interface NavChild {
  label: string;
  /** Optional when onClick is provided instead of a plain href. */
  to?: string;
  /** Link to the closest existing page (no dedicated page yet) — never marks its group as active. */
  fallback?: boolean;
  /** Absolute URL opened in a new tab instead of a router link. */
  external?: boolean;
  icon?: React.ReactNode;
  /** Renders as a button instead of a Link/anchor; used for auth-gated actions like Learning Hub. */
  onClick?: () => void;
  disabled?: boolean;
}

interface NavGroup {
  label: string;
  children: NavChild[];
  /** Shown as a column inside the "More" panel instead of as its own top-level item. */
  inMore?: boolean;
}

const CONTACT_US = '/contact-us';
const PROFESSIONAL_SERVICES_ENQUIRY = `${ROUTES.PUBLIC.HOME}?professionalServices=1`;
/** Opens the enquiry form with its Service dropdown pre-selected — code must match a
 *  DictionaryValueMaster.Code under the "ProfessionalServices" dictionary type. */
const professionalServiceEnquiry = (code: string) =>
  `${PROFESSIONAL_SERVICES_ENQUIRY}&service=${encodeURIComponent(code)}`;

const buildNavGroups = (
  loggedIn: boolean,
  role?: string | null,
  onOpenLearningHub?: () => void,
  learningHubLoading?: boolean,
): NavGroup[] => [
  {
    label: 'For Students',
    children: [
      { label: 'Internships', to: ROUTES.PUBLIC.INTERNSHIPS },
      { label: 'Jobs', to: ROUTES.PUBLIC.JOBS },
      { label: 'Mentorship', to: ROUTES.PUBLIC.MENTORSHIPS },
      { label: 'Programs / Events', to: ROUTES.PUBLIC.PROGRAMS },
      {
        label: learningHubLoading ? 'Opening…' : 'Learning Hub',
        onClick: onOpenLearningHub,
        disabled: learningHubLoading,
      },
    ],
  },
  {
    label: 'For Institutions',
    children: [
      { label: 'eCAP Program', to: CONTACT_US, fallback: true },
      { label: 'Industry Connect', to: CONTACT_US, fallback: true },
      { label: 'Hackathons', to: ROUTES.PUBLIC.PROGRAMS, fallback: true },
      { label: 'Student Development', to: CONTACT_US, fallback: true },
      { label: 'Partner With Us', to: CONTACT_US, fallback: true },
    ],
  },
  {
    label: 'For Organizations',
    children: [
      {
        label: 'Post Opportunities',
        to: loggedIn && role === 'ORGANIZATION' ? ROUTES.ORGANIZATION.DASHBOARD : '/register?type=organization',
        fallback: true,
      },
      { label: 'Campus Engagement', to: CONTACT_US, fallback: true },
      { label: 'Talent Hub', to: CONTACT_US, fallback: true },
      { label: 'Partner With Us', to: CONTACT_US, fallback: true },
    ],
  },
  {
    label: 'Professional Services',
    inMore: true,
    children: [
      { label: 'Curriculum Advisory', to: professionalServiceEnquiry('CURRICULUM_ADVISORY'), fallback: true },
      { label: 'Faculty Development', to: professionalServiceEnquiry('FACULTY_DEVELOPMENT'), fallback: true },
      { label: 'Corporate Training', to: professionalServiceEnquiry('CORPORATE_TRAINING'), fallback: true },
      { label: 'Industry Connect', to: professionalServiceEnquiry('INDUSTRY_CONNECT'), fallback: true },
      { label: 'Research & Projects', to: professionalServiceEnquiry('RESEARCH_AND_PROJECTS'), fallback: true },
      {
        label: 'Career & Placement Support',
        to: professionalServiceEnquiry('CAREER_AND_PLACEMENT_SUPPORT'),
        fallback: true,
      },
    ],
  },
  {
    label: 'Sponsorship',
    inMore: true,
    children: [
      { label: 'Why Sponsor', to: CONTACT_US, fallback: true },
      { label: 'Partnership Options', to: CONTACT_US, fallback: true },
      { label: 'Our Sponsors', to: '/our-partners' },
      { label: 'Impact Stories', to: '/accolades' },
    ],
  },
  {
    label: 'Company',
    inMore: true,
    children: [
      { label: 'About Us', to: '/about-us' },
      { label: 'News & Media', to: ROUTES.PUBLIC.NEWS_MEDIA },
      { label: 'Contact Us', to: CONTACT_US },
    ],
  },
];

const SOCIAL_GROUP: NavGroup = {
  label: 'Social',
  inMore: true,
  children: [
    { label: 'Community Hub', to: ROUTES.PUBLIC.COMMUNITY, icon: <TeamOutlined /> },
    { label: 'LinkedIn', to: SOCIAL_LINKS.linkedin, external: true, icon: <LinkedinOutlined /> },
    { label: 'X (Twitter)', to: SOCIAL_LINKS.x, external: true, icon: <XOutlined /> },
    { label: 'Instagram', to: SOCIAL_LINKS.instagram, external: true, icon: <InstagramOutlined /> },
    {
      label: 'Facebook',
      to: SOCIAL_LINKS.facebook,
      external: true,
      icon: <FacebookOutlined />,
    },
  ],
};

const renderChildLink = (groupLabel: string, child: NavChild) => {
  const key = `${groupLabel}-${child.label}`;
  const content = (
    <>
      {child.icon && (
        <span className="enp-nav__icon" aria-hidden="true">
          {child.icon}
        </span>
      )}
      {child.label}
    </>
  );
  if (child.onClick) {
    return (
      <button key={key} type="button" className="enp-nav__panel-btn" onClick={child.onClick} disabled={child.disabled}>
        {content}
      </button>
    );
  }
  return child.external ? (
    <a key={key} href={child.to} target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  ) : (
    <Link key={key} to={child.to as string}>
      {content}
    </Link>
  );
};

const MORE_LABEL = 'More';

const pathOf = (to: string): string => to.split('?')[0];

/** Dropdown hover is CSS-only on desktop with a real pointer; everywhere else the trigger toggles it. */
const hoverMenusSupported = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(min-width: 1101px) and (hover: hover)').matches;

const Navbar: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn());
  const [user, setUser] = useState(getUser());
  const [headerProfilePicUrl, setHeaderProfilePicUrl] = useState<string | undefined>(undefined);
  const [headerAvatarError, setHeaderAvatarError] = useState(false);
  const headerObjectUrlRef = useRef<string | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = getUserRole();

  useEffect(() => {
    // Listen for login/logout in other tabs
    const handleStorage = () => {
      setLoggedIn(isLoggedIn());
      setUser(getUser());
      setHeaderProfilePicUrl(undefined);
      setHeaderAvatarError(false);
    };
    window.addEventListener('storage', handleStorage);

    // Listen for login/logout in the same tab
    const handleAuthChange = () => {
      setLoggedIn(isLoggedIn());
      setUser(getUser());
      setHeaderProfilePicUrl(undefined);
      setHeaderAvatarError(false);
    };
    window.addEventListener('authChange', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, []);

  /** Load profile photo via authenticated content endpoint (reliable for private S3 objects). */
  useEffect(() => {
    if (!loggedIn || !user?.email) return;
    const rolesWithPersonalProfile = ['STUDENT', 'MENTOR', 'SPONSOR', 'ORGANIZATION'];
    if (!userRole || !rolesWithPersonalProfile.includes(userRole)) {
      return;
    }

    let cancelled = false;

    const revokeHeaderObjectUrl = () => {
      if (headerObjectUrlRef.current) {
        URL.revokeObjectURL(headerObjectUrlRef.current);
        headerObjectUrlRef.current = undefined;
      }
    };

    const loadAvatar = async () => {
      const token = getToken();
      if (!token) return;
      revokeHeaderObjectUrl();
      setHeaderAvatarError(false);
      setHeaderProfilePicUrl(undefined);

      try {
        const res = await fetch(apiUrls.userProfile.personalInfo.profilePictureContent, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.ok) {
          const blob = await res.blob();
          if (blob.size > 0) {
            const objectUrl = URL.createObjectURL(blob);
            headerObjectUrlRef.current = objectUrl;
            setHeaderProfilePicUrl(objectUrl);
            return;
          }
        }
      } catch {
        /* fall through to presigned URL */
      }

      try {
        const info = await UserProfileService.getPersonalInfo();
        if (cancelled || info.status !== 'success' || !info.data?.profilePicUrl) return;
        const url = info.data.profilePicUrl.trim();
        if (!url) return;
        setHeaderProfilePicUrl(url);
        const cur = getUser();
        if (cur?.email === user.email) {
          const sessionToken = getToken();
          if (sessionToken) {
            setSession(sessionToken, { ...cur, profilePicUrl: url }, getRefreshToken() || undefined);
          }
        }
      } catch {
        /* ignore */
      }
    };

    void loadAvatar();

    return () => {
      cancelled = true;
      revokeHeaderObjectUrl();
    };
  }, [loggedIn, user?.email, user?.profilePicUrl, userRole]);

  const profileCompletionPercent =
    userRole === 'STUDENT' ? parseProfileCompletionPercent(user) : null;

  const profileCompletionColor =
    profileCompletionPercent != null &&
    profileCompletionPercent >= STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD
      ? '#52c41a'
      : '#faad14';

  const showProfileCompletionWarning =
    profileCompletionPercent != null &&
    profileCompletionPercent < STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD;

  const headerAvatarSrc =
    headerAvatarError || !headerProfilePicUrl?.trim()
      ? DEFAULT_NAV_PLACEHOLDER_SRC
      : headerProfilePicUrl.trim();

  const menu = (
    <Menu>
      <Menu.Item key="profile" onClick={() => {
        if (userRole === 'ORGANIZATION') {
          navigate(ROUTES.ORGANIZATION.PROFILE);
        } else if (userRole === 'MENTOR') {
          navigate(ROUTES.MENTOR.PROFILE);
        } else if (userRole === 'SPONSOR') {
          navigate(ROUTES.SPONSOR.PROFILE);
        } else {
          navigate(ROUTES.STUDENT.PROFILE);
        }
      }}>
        My Profile
      </Menu.Item>
      <Menu.Item key="inbox" icon={<InboxOutlined />} onClick={() => navigate('/inbox')}>
        Inbox
      </Menu.Item>
      {userRole === 'ORGANIZATION' && (
        <Menu.Item key="subscription" icon={<CrownOutlined />} onClick={() => navigate(ROUTES.ORGANIZATION.SUBSCRIPTION)}>
          Subscription
        </Menu.Item>
      )}
      <Menu.Item key="logout" onClick={logout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  // State for login/registration modals and mobile menu
  const [loginVisible, setLoginVisible] = React.useState(false);
  const [registerVisible, setRegisterVisible] = React.useState(false);
  const [loginDefaultTab, setLoginDefaultTab] = React.useState<'student' | 'employer' | 'mentor'>('student');
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [openGroup, setOpenGroup] = React.useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const isGroupActive = (group: NavGroup): boolean =>
    group.children.some((c) => !c.fallback && !c.external && c.to && location.pathname.startsWith(pathOf(c.to)));


  const renderGroup = (group: NavGroup) => {
    const open = openGroup === group.label;
    return (
      <div key={group.label} className={`enp-nav__item ${open ? 'open' : ''}`}>
        <button
          type="button"
          className={`enp-nav__link enp-nav__trigger ${isGroupActive(group) ? 'active' : ''}`}
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => toggleGroup(group.label)}
        >
          {group.label} <span className="enp-caret" aria-hidden="true" />
        </button>
        <div className="enp-nav__panel">{group.children.map((child) => renderChildLink(group.label, child))}</div>
      </div>
    );
  };

  const closeMenus = () => {
    setMobileMenuOpen(false);
    setOpenGroup(null);
  };

  // Close menus on navigation, outside click and Escape.
  useEffect(() => {
    closeMenus();
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) closeMenus();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenus();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const toggleGroup = (label: string) => {
    if (hoverMenusSupported()) return;
    setOpenGroup((cur) => (cur === label ? null : label));
  };

  // Handlers to switch between login and registration
  const handleShowLogin = (tab: 'student' | 'employer' | 'mentor' = 'student') => {
    setRegisterVisible(false);
    setLoginDefaultTab(tab);
    setLoginVisible(true);
  };
  const handleShowRegister = () => {
    setLoginVisible(false);
    setRegisterVisible(true);
  };

  // Opens as a normal in-app route (/learning-hub) so Navbar/Footer stay put — the page itself
  // handles the student-login gate and fetching CloudFront access cookies before showing the
  // iframe, so this just needs to navigate there.
  const handleOpenLearningHub = () => {
    navigate(ROUTES.PUBLIC.LEARNING_HUB);
  };

  const allGroups = buildNavGroups(loggedIn, userRole, handleOpenLearningHub, false);
  const primaryGroups = allGroups.filter((g) => !g.inMore);
  const moreGroups = [...allGroups.filter((g) => g.inMore), SOCIAL_GROUP];
  const moreActive = moreGroups.some(isGroupActive);
  const showDashboard = Boolean(
    loggedIn && userRole && ['ORGANIZATION', 'STUDENT', 'MENTOR', 'SPONSOR'].includes(userRole),
  );

  return (
    <header className="enp-header">
      <div className="enp-header__row">
        <Link to="/" className="enp-brand" onClick={closeMenus}>
          <img src="/logo.svg" alt="enpropeL" className="enp-brand__logo" />
          <span className="enp-brand__tag">
            Bridging Students with Industry.
            <br />
            Building Careers for the Future.
          </span>
        </Link>

        <button
          type="button"
          className={`enp-toggle ${mobileMenuOpen ? 'is-open' : ''}`}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
          onClick={() => {
            setMobileMenuOpen((open) => !open);
            setOpenGroup(null);
          }}
        >
          <span />
          <span />
          <span />
        </button>

        <nav
          ref={navRef}
          className={`enp-nav ${mobileMenuOpen ? 'is-open' : ''}`}
          aria-label="Primary"
        >
          {showDashboard && userRole && (
            <div className="enp-nav__item">
              <NavLink
                to={getDashboardPath(userRole)}
                className={`enp-nav__link ${isDashboardNavActive(userRole, location.pathname) ? 'active' : ''}`}
              >
                My Dashboard
              </NavLink>
            </div>
          )}
          <div className="enp-nav__item">
            <NavLink to={ROUTES.PUBLIC.HOME} end className="enp-nav__link">
              Home
            </NavLink>
          </div>

          {primaryGroups.map(renderGroup)}

          <div className={`enp-nav__item enp-nav__item--more ${openGroup === MORE_LABEL ? 'open' : ''}`}>
            <button
              type="button"
              className={`enp-nav__link enp-nav__trigger ${moreActive ? 'active' : ''}`}
              aria-haspopup="true"
              aria-expanded={openGroup === MORE_LABEL}
              onClick={() => toggleGroup(MORE_LABEL)}
            >
              {MORE_LABEL} <span className="enp-caret" aria-hidden="true" />
            </button>
            <div className="enp-nav__panel enp-nav__panel--mega">
              {moreGroups.map((group) => (
                <div key={group.label} className="enp-nav__section">
                  <div className="enp-nav__heading">{group.label}</div>
                  {group.children.map((child) => renderChildLink(group.label, child))}
                </div>
              ))}
            </div>
          </div>

        </nav>

        <div className="enp-actions">
          <div className="enp-actions__row">
            {loggedIn ? (
              <>
                <NotificationDropdown />
                <Dropdown overlay={menu} placement="bottomRight" trigger={["click"]}>
                  <div className="navbar-user-menu">
                    <div className="navbar-user-text">
                      <span className="user-name">
                        {user?.fullname || user?.email || user?.userUniqueId || 'My account'}
                      </span>
                      {profileCompletionPercent != null && (
                        <div className="navbar-profile-completion" title="Profile completion">
                          <Progress
                            percent={profileCompletionPercent}
                            showInfo={false}
                            size="small"
                            strokeColor={profileCompletionColor}
                            trailColor="rgba(255, 255, 255, 0.2)"
                            className="navbar-profile-completion-bar"
                          />
                          <span
                            className="navbar-profile-completion-label"
                            style={{ color: profileCompletionColor }}
                          >
                            Profile {profileCompletionPercent}% complete
                          </span>
                        </div>
                      )}
                    </div>
                    <Avatar
                      key={headerAvatarSrc}
                      size="large"
                      className="navbar-user-avatar"
                      style={{ verticalAlign: 'middle', flexShrink: 0 }}
                      src={headerAvatarSrc}
                      onError={() => {
                        setHeaderAvatarError(true);
                        setHeaderProfilePicUrl(undefined);
                        return false;
                      }}
                    />
                  </div>
                </Dropdown>
              </>
            ) : (
              <>
                <button type="button" className="enp-btn enp-btn--outline" onClick={() => handleShowLogin('student')}>
                  Login
                </button>
                <button type="button" className="enp-btn enp-btn--primary" onClick={handleShowRegister}>
                  Join enpropeL
                </button>
                <MenuLogin
                  visible={loginVisible}
                  loading={false}
                  onCancel={() => {
                    setLoginVisible(false);
                    localStorage.removeItem('activeTab');
                  }}
                  onRegister={handleShowRegister}
                  defaultTab={loginDefaultTab}
                />
                <MenuRegistration
                  visible={registerVisible}
                  loading={false}
                  onCancel={() => {
                    setRegisterVisible(false);
                    localStorage.removeItem('activeTab');
                  }}
                  onLogin={() => handleShowLogin('student')}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
